import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Sparkles, GraduationCap, ShoppingBag, Mail, Users, Award, Contact, LogOut, ChevronRight, Inbox, UserCircle, LifeBuoy, BookOpen, PenLine, Store, Video, Pill, TrendingUp, HandHeart, Music, ShoppingCart, Palette, DollarSign, User, Footprints, UserCheck, Crown, NotebookPen } from "lucide-react";
import LogoText from "../components/LogoText";
import HelpMeButton from "../components/HelpMeButton";
import Overview from "../components/dashboard/Overview";
import MyStoreTab from "../components/dashboard/MyStoreTab";
import MyEarningsTab from "../components/dashboard/MyEarningsTab";
import MyCoursesTab from "../components/dashboard/MyCoursesTab";
// FunnelsTab removed Feb 2026 — folded into Mirror Site for simplicity.
import CoursesTab from "../components/dashboard/CoursesTab";
import StoreTab from "../components/dashboard/StoreTab";
import GoliDashboardTab from "../components/dashboard/GoliDashboardTab";
import EmailsTab from "../components/dashboard/EmailsTab";
import ContactsTab from "../components/dashboard/ContactsTab";
import CommunityTab from "../components/dashboard/CommunityTab";
import AffiliatesTab from "../components/dashboard/AffiliatesTab";
import LeadsTab from "../components/dashboard/LeadsTab";
import ProfileTab from "../components/dashboard/ProfileTab";
import HelpdeskTab from "../components/dashboard/HelpdeskTab";
import AffiliateManagerTab from "../components/dashboard/AffiliateManagerTab";
import NotesTab from "../components/dashboard/NotesTab";
import CourseBuilderTab from "../components/dashboard/CourseBuilderTab";
import TemplateBuilderTab from "../components/dashboard/TemplateBuilderTab";
import FeaturedProductsTab from "../components/dashboard/FeaturedProductsTab";
import PrayerInboxTab from "../components/dashboard/PrayerInboxTab";
import JukeboxAdminTab from "../components/dashboard/JukeboxAdminTab";
// LessonVideosAdminTab deleted Feb 2026 (Mike: "forget all that coach certification and all of it").
import AttributionTab from "../components/dashboard/AttributionTab";
import MirrorSiteTab from "../components/dashboard/MirrorSiteTab";
import LinksTab from "../components/dashboard/LinksTab";
import MyMirrorsTab from "../components/dashboard/MyMirrorsTab";
import {
  MarketTab, AcademyTab, AcademyCourseTab, Affiliate101Tab, BookTab, PricingTab,
  WalkTab, JukeboxTab, ColoringPagesTab, DirectoryTab, FounderTab,
} from "../components/dashboard/EmbeddedPageTabs";
import Brand from "../components/Brand";

/* ════════════════════════════════════════════════════════════════════
   SIDEBAR — organised by PURPOSE, with one COLOUR per group so anyone
   (especially Mike's older readers) can find anything in 3 seconds.

   Mike's organising principle (his words):
     • Everything for SALE  → 🟠 SHOP        (orange)
     • Everything for EDUCATION → 🟢 LEARN   (green)
     • Everything to BUILD their site → 🟦 BUILD  (teal/blue)
     • Free family content → 🔴 FREE         (red)
     • People stuff → 🟣 CONNECT             (purple)
     • Admin → 👑 MIKE'S OFFICE              (wine, admin-only)
   ════════════════════════════════════════════════════════════════════ */

const SHOP_TABS = [
  { to: "/dashboard/market",    icon: ShoppingCart, label: "Market" },
  { to: "/dashboard/goli",      icon: Pill,         label: "Supplements" },
  { to: "/dashboard/store",     icon: ShoppingBag,  label: "Member Store" },
];

const LEARN_TABS = [
  { to: "/dashboard/the-book",     icon: BookOpen,      label: "★ The Book — With Faith" },
  { to: "/dashboard/academy",      icon: GraduationCap, label: "★ The Academy" },
  { to: "/dashboard/my-courses",   icon: Video,         label: "My Courses" },
];

const BUILD_TABS = [
  { to: "/dashboard/my-mirrors",   icon: Sparkles,    label: "★ Mirror Site Builder" },
  { to: "/dashboard/mirror-site",  icon: Sparkles,    label: "Funnel Templates" },
  { to: "/dashboard/ai-writer",    icon: BookOpen,    label: "★ AI WRITER · Full-Page" },
  { to: "/dashboard/notes",        icon: NotebookPen, label: "★ Pro Notes" },
  { to: "/dashboard/my-store",     icon: Store,       label: "My Market" },
  { to: "/dashboard/my-products",  icon: ShoppingBag, label: "★ My Products" },
  { to: "/dashboard/emails",       icon: Mail,        label: "Email Engine" },
  { to: "/dashboard/leads",        icon: Inbox,       label: "Sign-Ups (Leads)" },
  { to: "/dashboard/contacts",     icon: Contact,     label: "Contacts" },
  { to: "/dashboard/affiliates",   icon: Award,       label: "Affiliate Stats" },
  { to: "/dashboard/my-earnings",  icon: DollarSign,  label: "★ My Earnings · $" },
];

const FREE_TABS = [
  { to: "/dashboard/coloring-pages",  icon: Palette,     label: "Kids · Coloring Pages" },
  { to: "/dashboard/walk",            icon: Footprints,  label: "Walk Tracker" },
  { to: "/dashboard/jukebox",         icon: Music,       label: "Jukebox" },
];

const CONNECT_TABS = [
  { to: "/dashboard/directory",  icon: Users,    label: "Members Directory" },
  { to: "/dashboard/community",  icon: Users,    label: "Community Board" },
  { to: "/dashboard/founder",    icon: User,     label: "Our Mission" },
  { to: "/dashboard/helpdesk",   icon: LifeBuoy, label: "Get Help" },
];

const ADMIN_TABS = [
  { to: "/dashboard/vendor-sales",     icon: ShoppingBag,  label: "★ Vendor Sales Ledger" },
  { to: "/admin/tutorials",            icon: Video,        label: "★ Record Walk-Through Videos", external: true },
  { to: "/dashboard/attribution",      icon: UserCheck,    label: "Recent Signups · Credit" },
  { to: "/dashboard/prayer-inbox",     icon: HandHeart,    label: "Prayer Inbox" },
  { to: "/dashboard/jukebox-admin",    icon: Music,        label: "Mike's Jukebox" },
  { to: "/dashboard/lesson-videos",    icon: Video,        label: "Lesson Videos" },
  { to: "/dashboard/course-builder",   icon: GraduationCap,label: "Course Builder" },
  { to: "/dashboard/template-builder", icon: Sparkles,     label: "Template Builder" },
  { to: "/dashboard/featured-products",icon: ShoppingBag,  label: "Featured Products" },
  { to: "/dashboard/affiliate-manager",icon: Award,        label: "Affiliate Manager" },
];

/* Group colour palette — one hue per purpose. */
const GROUPS = {
  shop:    { color: "#F39A1F", title: "Market",          sub: "Everything for sale",           testid: "sidebar-section-shop" },
  learn:   { color: "#2E9F3A", title: "Academy",         sub: "Learn how to use every tool",   testid: "sidebar-section-learn" },
  build:   { color: "#287F8E", title: "Build Your Site", sub: "Tools that earn you money",     testid: "sidebar-section-build" },
  free:    { color: "#E33E3E", title: "Free Goodies",    sub: "For the family",                testid: "sidebar-section-free" },
  connect: { color: "#7C3AED", title: "Connect",         sub: "People · Help · Community",     testid: "sidebar-section-connect" },
  admin:   { color: "#0F172A", title: "Mike's Office",   sub: "Admin only",                    testid: "sidebar-section-admin" },
};

/* Greeting first-name — bulletproof override for the founder's account.
   Catches:
     • cached JWT sessions with the old "Mike" full_name
     • typos like "Micahel" (transposed) or "Micheal" (swapped)
     • case-insensitive email match
   Falls back to whatever's stored if it's not the founder. */
function greetFirstName(user) {
  if (!user) return "friend";
  const email = (user.email || "").toLowerCase().trim();
  const isFounder = user.is_admin === true || email === "mylivegoodplan@gmail.com";
  if (isFounder) return "Michael";
  return (user.full_name || "friend").split(" ")[0] || "friend";
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const loc = useLocation();
  // Helpdesk shows as "Helpdesk" for admin and "Get Help" for everyone else (label already set above as "Get Help")
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    api.get("/dashboard/overview").then(r => setOverview(r.data)).catch(()=>{});
  }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-ivory" data-testid="dashboard-shell">
      {/* Single floating "Back to Office" pill — only shows when NOT on the
          Overview. This is the ONLY navigation chrome a member sees outside
          the back office itself. Top-left, bright, idiot-proof. */}
      {loc.pathname !== "/dashboard" && loc.pathname !== "/dashboard/" && (
        <Link to="/dashboard"
              data-testid="back-to-office"
              className="fixed top-4 left-4 z-50 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-bold shadow-lg hover:scale-[1.04] transition"
              style={{ background: "#EAF6FC", color: "#0F172A", border: "2px solid #2E9F3A" }}>
          <LayoutDashboard className="w-4 h-4"/> Back To My Office
        </Link>
      )}
      {/* Sign-out — top-right, small, only on the Overview itself (so it's
          easy to find on the home page but doesn't clutter sub-tabs). */}
      {(loc.pathname === "/dashboard" || loc.pathname === "/dashboard/") && (
        <button onClick={logout}
                data-testid="dashboard-logout"
                className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-bold transition shadow hover:scale-[1.03]"
                style={{ background: "#FFFFFF", color: "#0F172A", border: "2px solid #0F172A" }}>
          <LogOut className="w-4 h-4"/> Sign Out
        </button>
      )}
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8 pb-32" data-testid="dashboard-main">
        <Routes>
          <Route index element={<Overview data={overview} />} />
          <Route path="market" element={<MarketTab />} />
          <Route path="academy" element={<AcademyTab />} />
          <Route path="academy/:slug" element={<AcademyCourseTab />} />
          <Route path="affiliate-101" element={<Affiliate101Tab />} />
          <Route path="the-book" element={<BookTab />} />
          <Route path="pricing" element={<PricingTab />} />
          <Route path="walk" element={<WalkTab />} />
          <Route path="jukebox" element={<JukeboxTab />} />
          <Route path="coloring-pages" element={<ColoringPagesTab />} />
          <Route path="directory" element={<DirectoryTab />} />
          <Route path="founder" element={<FounderTab />} />
          <Route path="faith-writer" element={<Navigate to="/dashboard/ai-writer" replace />} />
          <Route path="notes" element={<NotesTab />} />
          <Route path="my-store" element={<MyStoreTab />} />
          <Route path="my-courses" element={<MyCoursesTab />} />
          <Route path="pro-vault" element={<Navigate to="/dashboard/academy" replace />} />
          <Route path="profile" element={<ProfileTab />} />
          <Route path="leads" element={<LeadsTab />} />
          <Route path="funnels" element={<Navigate to="/dashboard/mirror-site" replace />} />
          <Route path="funnels/*" element={<Navigate to="/dashboard/mirror-site" replace />} />
          <Route path="store" element={<StoreTab />} />
          <Route path="goli" element={<GoliDashboardTab />} />
          <Route path="emails" element={<EmailsTab />} />
          <Route path="links" element={<LinksTab />} />
          <Route path="my-mirrors" element={<MyMirrorsTab />} />
          <Route path="my-earnings" element={<MyEarningsTab />} />
          <Route path="contacts" element={<ContactsTab />} />
          <Route path="community" element={<CommunityTab />} />
          <Route path="affiliates" element={<AffiliatesTab />} />
          <Route path="helpdesk" element={<HelpdeskTab />} />
          <Route path="affiliate-manager" element={<AffiliateManagerTab />} />
          <Route path="attribution" element={<AttributionTab />} />
          <Route path="mirror-site" element={<MirrorSiteTab />} />
          <Route path="course-builder" element={<CourseBuilderTab />} />
          <Route path="template-builder" element={<TemplateBuilderTab />} />
          <Route path="featured-products" element={<FeaturedProductsTab />} />
          <Route path="prayer-inbox" element={<PrayerInboxTab />} />
          <Route path="jukebox-admin" element={<JukeboxAdminTab />} />
          <Route path="lesson-videos" element={<Navigate to="/dashboard" replace />} />
          {/* Legacy dashboard URL aliases — auto-redirect to current names */}
          <Route path="apologetics" element={<Navigate to="/dashboard" replace />} />
          <Route path="courses" element={<Navigate to="/dashboard/my-courses" replace />} />
          <Route path="books" element={<Navigate to="/dashboard" replace />} />
          <Route path="book-store" element={<Navigate to="/dashboard" replace />} />
          <Route path="book-approvals" element={<Navigate to="/dashboard" replace />} />
          <Route path="faith-mentor" element={<Navigate to="/dashboard/ask-mike" replace />} />
          <Route path="mentor" element={<Navigate to="/dashboard/ask-mike" replace />} />
          <Route path="earnings" element={<Navigate to="/dashboard/my-earnings" replace />} />
          {/* Catch-all — any unknown /dashboard/* path goes back to Overview
              instead of rendering a blank screen. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      <HelpMeButton />
    </div>
  );
}
